import { useState } from "react";
import { useNavigate } from "react-router";
import type { Project } from "../api/types";
import { useLang, useT } from "../i18n";
import { colorHex, PALETTE } from "../lib/colors";
import { flattenTree, keyBetween, projectTree } from "../lib/tasks";
import { useApp, useSyncState } from "../store";
import { addProject, archiveProject, deleteProject, moveProject, updateProject } from "../sync/actions";
import { IconArchive, IconArrowDown, IconArrowUp, IconEdit, IconHeart, IconMore, IconTrash } from "../ui/icons";
import { Confirm, Menu, Modal, ModalHeader, useAnchor } from "../ui/primitives";

export interface ProjectFormValue {
  name: string;
  color: string;
  parentId: string | null;
  isFavorite: boolean;
  viewStyle: string;
}

/** Add / edit project dialog (name, color, parent, favorite, layout). */
export function ProjectDialog({
  project,
  initialParent,
  orderKey,
  onClose,
}: {
  project?: Project;
  initialParent?: string | null;
  orderKey?: string;
  onClose: () => void;
}) {
  const t = useT();
  const lang = useLang();
  const state = useSyncState();
  const navigate = useNavigate();
  const [v, setV] = useState<ProjectFormValue>({
    name: project?.name ?? "",
    color: project?.color ?? "charcoal",
    parentId: project ? project.parent_id : (initialParent ?? null),
    isFavorite: project?.is_favorite ?? false,
    viewStyle: project?.view_style ?? "list",
  });
  const candidates = flattenTree(
    projectTree(
      Object.values(state.projects).filter((p) => !p.inbox_project && p.id !== project?.id && !p.workspace_id),
    ),
  );
  const submit = () => {
    if (!v.name.trim()) return;
    if (project) {
      void updateProject(project.id, {
        name: v.name.trim(),
        color: v.color,
        is_favorite: v.isFavorite,
        view_style: v.viewStyle,
      });
      if (v.parentId !== project.parent_id) void moveProject(project.id, v.parentId);
    } else {
      const id = addProject({
        name: v.name.trim(),
        color: v.color,
        parent_id: v.parentId,
        is_favorite: v.isFavorite,
        view_style: v.viewStyle,
        order_key: orderKey,
      });
      navigate(`/app/project/${id}`);
    }
    onClose();
  };
  return (
    <Modal onClose={onClose} center label={project ? t("editProject") : t("addProject")}>
      <ModalHeader title={project ? t("editProject") : t("addProject")} onClose={onClose} />
      <form
        className="modal-body"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label className="field">
          <span>{t("name")}</span>
          <input
            className="input"
            autoFocus
            value={v.name}
            maxLength={120}
            data-testid="project-name-input"
            onChange={(e) => setV({ ...v, name: e.target.value })}
          />
        </label>
        <label className="field">
          <span>{t("color")}</span>
          <select
            className="input"
            value={v.color}
            onChange={(e) => setV({ ...v, color: e.target.value })}
            style={{ color: colorHex(v.color) }}
          >
            {PALETTE.map((c) => (
              <option key={c.name} value={c.name} style={{ color: c.hex }}>
                ● {lang === "zh" ? c.zh : c.en}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t("parentProject")}</span>
          <select
            className="input"
            value={v.parentId ?? ""}
            onChange={(e) => setV({ ...v, parentId: e.target.value || null })}
          >
            <option value="">{t("noParent")}</option>
            {candidates.map((n) => (
              <option key={n.project.id} value={n.project.id}>
                {"  ".repeat(n.depth)}
                {n.project.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field" style={{ flexDirection: "row", alignItems: "center" }}>
          <input
            type="checkbox"
            checked={v.isFavorite}
            onChange={(e) => setV({ ...v, isFavorite: e.target.checked })}
          />
          <span style={{ fontWeight: 400 }}>{t("addToFavorites")}</span>
        </label>
        <div className="field">
          <span>{t("layout")}</span>
          <div style={{ display: "flex", gap: 8 }}>
            {(["list", "board", "calendar"] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={`btn btn-chip${v.viewStyle === s ? " active" : ""}`}
                style={{ borderColor: v.viewStyle === s ? "var(--accent)" : undefined }}
                onClick={() => setV({ ...v, viewStyle: s })}
              >
                {t(s)}
              </button>
            ))}
          </div>
        </div>
        <div className="modal-footer" style={{ margin: "0 -16px -16px" }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t("cancel")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={!v.name.trim()} data-testid="project-submit">
            {project ? t("save") : t("add")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Project context menu (sidebar "…" / right-click, project header). */
export function ProjectMenu({
  project,
  anchor,
  onClose,
}: {
  project: Project;
  anchor: Element | null;
  onClose: () => void;
}) {
  const t = useT();
  const state = useSyncState();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<null | { kind: "edit" } | { kind: "add"; orderKey: string }>(null);
  const [confirm, setConfirm] = useState<null | "delete" | "archive">(null);
  const siblings = Object.values(state.projects)
    .filter(
      (p) =>
        (p.parent_id ?? null) === (project.parent_id ?? null) &&
        !p.inbox_project &&
        p.workspace_id === project.workspace_id,
    )
    .sort((a, b) => ((a.order_key ?? "") < (b.order_key ?? "") ? -1 : a.child_order - b.child_order));
  const idx = siblings.findIndex((p) => p.id === project.id);
  return (
    <>
      {anchor && (
        <Menu
          anchor={anchor}
          onClose={onClose}
          items={[
            {
              key: "above",
              label: t("addProjectAbove"),
              icon: <IconArrowUp size={18} />,
              onSelect: () => setDialog({ kind: "add", orderKey: keyBetween(siblings[idx - 1], project) }),
            },
            {
              key: "below",
              label: t("addProjectBelow"),
              icon: <IconArrowDown size={18} />,
              onSelect: () => setDialog({ kind: "add", orderKey: keyBetween(project, siblings[idx + 1]) }),
            },
            { key: "s1", label: "", separator: true },
            {
              key: "edit",
              label: t("edit"),
              icon: <IconEdit size={18} />,
              onSelect: () => setDialog({ kind: "edit" }),
            },
            {
              key: "fav",
              label: project.is_favorite ? t("removeFromFavorites") : t("addToFavorites"),
              icon: <IconHeart size={18} />,
              onSelect: () => void updateProject(project.id, { is_favorite: !project.is_favorite }),
            },
            { key: "s2", label: "", separator: true },
            {
              key: "archive",
              label: t("archive"),
              icon: <IconArchive size={18} />,
              onSelect: () => setConfirm("archive"),
            },
            {
              key: "delete",
              label: t("delete"),
              icon: <IconTrash size={18} />,
              danger: true,
              onSelect: () => setConfirm("delete"),
            },
          ]}
        />
      )}
      {dialog?.kind === "edit" && <ProjectDialog project={project} onClose={() => setDialog(null)} />}
      {dialog?.kind === "add" && (
        <ProjectDialog initialParent={project.parent_id} orderKey={dialog.orderKey} onClose={() => setDialog(null)} />
      )}
      {confirm && (
        <Confirm
          message={
            confirm === "delete"
              ? t("deleteProjectConfirm", { name: project.name })
              : `${t("archive")} “${project.name}”?`
          }
          confirmLabel={confirm === "delete" ? t("delete") : t("archive")}
          cancelLabel={t("cancel")}
          onConfirm={() => {
            if (window.location.pathname.includes(project.id)) navigate("/app/inbox");
            const inbox = useApp.getState().engine?.state.user?.inbox_project_id;
            void (confirm === "delete" ? deleteProject(project.id) : archiveProject(project.id));
            if (!inbox) navigate("/app/today");
          }}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}

export function ProjectMenuButton({ project }: { project: Project }) {
  const t = useT();
  const a = useAnchor();
  return (
    <>
      <button type="button" className="icon-btn" onClick={a.open} aria-label={t("more")} data-testid="project-menu">
        <IconMore size={20} />
      </button>
      <ProjectMenu project={project} anchor={a.anchor} onClose={a.close} />
    </>
  );
}
